import Button from '@/shared/components/rebranded/Button/Button';
import { useTranslations } from '@storebrand-digital/fms-translations-react';
import { FormEvent, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useCorporateContext } from '../../../CorporateContext';
import { URLs } from '../../../shared/URLs';
import { useScrollToTop } from '../../../shared/hooks';
import { RenderStep, StepProps } from '../Step/Step';
import styles from '../Wizard.module.css';
import { ClaimStepper } from './ClaimStepper/ClaimStepper';
import { ConditionalWrapper } from './ConditionalWrapper';
import { getButtonText, showStepper } from './helpers';

type StepRouteProps = StepProps & {
  // eslint-disable-next-line react/no-unused-prop-types
  path: string;
  render: RenderStep;
  index: number;
  childrenCount: number;
};

export const StepRoute = ({
  hideNextNavigation = false,
  hidePrevNavigation = false,
  hideExitNavigation = false,
  nextButtonText,
  prevButtonText,
  submitButtonText,
  canContinue = true,
  onNext = async () => {},
  onPrev = () => {},
  render,
  index,
  childrenCount,
  loading = false
}: StepRouteProps) => {
  const [nextClickCount, setNextClickCount] = useState(0);
  const { isCorporate } = useCorporateContext();
  const { pathname } = useLocation();
  const { getText } = useTranslations();

  useScrollToTop();

  const showNavigation =
    !hidePrevNavigation || !hideNextNavigation || !hideExitNavigation;
  const forceShowError = !canContinue && nextClickCount > 0;

  const handleNext = (e: FormEvent) => {
    e.preventDefault();
    if (nextClickCount === 0) {
      setNextClickCount(prevClickCount => prevClickCount + 1);
    }
    onNext(canContinue);
  };

  const resetNextClickCount = () => {
    setNextClickCount(0);
  };

  const startText = getText('wizard.start') || 'Start skademelding';
  const nextText = nextButtonText || getText('wizard.next');
  const submitText = submitButtonText || getText('wizard.submit');
  const buttonText = getButtonText(
    index,
    childrenCount,
    startText,
    nextText,
    submitText,
    pathname
  );

  const showDataTrackId = pathname.includes('intro/1')
    ? 'button-start_damage_report'
    : null;

  return (
    <ConditionalWrapper
      wrap={showStepper(pathname)}
      renderWrapper={children => (
        <ClaimStepper index={index} childrenCount={childrenCount}>
          {children}
        </ClaimStepper>
      )}
    >
      <ContentContainer>
        <form onSubmit={handleNext} noValidate>
          {render({
            forceShowError,
            canContinue,
            setNextClickCount,
            resetNextClickCount
          })}

          {showNavigation && (
            <div className={styles.navigationButtons}>
              {!hideNextNavigation && (
                <Button
                  color="contained"
                  data-trackid={showDataTrackId}
                  mr="16px"
                  disabled={forceShowError || loading}
                  loading={loading}
                  type="submit"
                  title={
                    canContinue ? '' : 'Rett opp feilene før du fortsetter'
                  }
                >
                  {buttonText}
                </Button>
              )}

              {hideNextNavigation && !hideExitNavigation && (
                <Button
                  as="a"
                  variant="outlined"
                  mr="16px"
                  href={
                    isCorporate
                      ? URLs.MELD_SKADE_BEDRIFT
                      : URLs.MELD_SKADE_PRIVAT
                  }
                >
                  Avslutt
                </Button>
              )}

              {!hidePrevNavigation &&
                (showStepper(pathname) ? (
                  <Button
                    type="button"
                    onClick={onPrev}
                    iconRight="arrow-up"
                    variant="text"
                    data-tracking-id="wizard-prev"
                  >
                    {prevButtonText || getText('wizard.prev')}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={onPrev}
                    iconLeft="arrow-left"
                    variant="text"
                    data-tracking-id="wizard-prev"
                  >
                    {prevButtonText || getText('wizard.prev')}
                  </Button>
                ))}
            </div>
          )}
        </form>
      </ContentContainer>
    </ConditionalWrapper>
  );
};

const ContentContainer = styled.div`
  max-width: 600px;
  @media (max-width: 768px) {
    margin: 24px 8px 8px;
  }
`;
